import { z } from "zod";

export const PAGE_CONFIG_SCHEMA = z.object({
  darkMode: z.boolean().optional(),
  simplifiedHeader: z.boolean().optional(),
  hideNavigation: z.boolean().optional(),
  hideBreadcrumbs: z.boolean().optional(),
  useHeadingTagForBreadcrumbs: z.boolean().optional(),
  overlayHeader: z.boolean().optional(),
  navigationThemeOverride: z.boolean().optional(),
  transparentHeaderTheme: z.enum(["light", "dark", "inherit"]).optional(),
  analytics: z.boolean().default(true).optional(),
  pageName: z.string().default("none").optional(),
  pageLoadTracking: z.enum(["none", "page_view"]).default("none").optional(),
});

export type PageConfigSchema = z.infer<typeof PAGE_CONFIG_SCHEMA>;

export type PageConfig = NonNullable<Required<PageConfigSchema>>;

export const DEFAULT_PAGE_CONFIG_VALUE: PageConfig = {
  darkMode: false,
  hideNavigation: false,
  simplifiedHeader: false,
  hideBreadcrumbs: false,
  useHeadingTagForBreadcrumbs: false,
  overlayHeader: false,
  analytics: false,
  pageName: "none",
  pageLoadTracking: "none",
  navigationThemeOverride: false,
  transparentHeaderTheme: "inherit",
};

const hasPageConfigProperties = (value: unknown): value is PageConfigSchema => {
  return PAGE_CONFIG_SCHEMA.safeParse(value).success;
};

export const getPageConfigProperties = (value: unknown): PageConfig => {
  if (!hasPageConfigProperties(value)) {
    return DEFAULT_PAGE_CONFIG_VALUE;
  }

  const {
    darkMode = false,
    hideNavigation = false,
    simplifiedHeader = false,
    hideBreadcrumbs = false,
    useHeadingTagForBreadcrumbs = false,
    overlayHeader = false,
    navigationThemeOverride = false,
    transparentHeaderTheme = "inherit",
    analytics = false,
    pageName = "none",
    pageLoadTracking = "none",
  } = value;

  return {
    darkMode,
    hideNavigation,
    simplifiedHeader,
    hideBreadcrumbs,
    useHeadingTagForBreadcrumbs,
    overlayHeader,
    navigationThemeOverride,
    transparentHeaderTheme,
    analytics,
    pageName,
    pageLoadTracking,
  };
};
